import { create } from 'zustand';
import * as api from '../lib/kdsApi';
import { NOT_MODIFIED, ApiError, AuthRequiredError } from '../lib/apiClient';
import { useAuthStore } from './authStore';
import { chime } from '../features/kds/sound';
import { setBranchLanguage } from '../lib/i18n';
import type {
  KdsAllDayRow,
  KdsLineStatus,
  KdsPriority,
  KdsSettings,
  KdsStation,
  KdsStreamEvent,
  KdsTicket,
  KdsViewMode,
} from '../types/kds';

// ─────────────────────────────────────────────────────────────────────────────
// KDS board state.
//
// THREE things in here are load-bearing and easy to get wrong later.
//
// 1. CLOCK SKEW. Every response carries the server's `serverTime`. We store the
//    difference against the local clock and compute every elapsed time through
//    it. Kitchen tablets are cheap, permanently on, and frequently minutes out;
//    a board that trusts Date.now() shows tickets as half an hour late the
//    moment they fire, cooks learn the colours are lies, and the SLA becomes
//    decoration. This is the single most important detail in the file.
//
// 2. PUSH FIRST, POLL AS A BACKSTOP. SSE carries whole tickets, so the normal
//    path never re-fetches — a bump repaints in the time it takes the socket to
//    deliver. Polling only wakes up when pings stop arriving, and stops again
//    as soon as they resume. We do NOT poll alongside the stream: that was the
//    old service screen's model and it means the board is only ever as fresh as
//    the poll interval no matter how fast the push was.
//
// 3. OPTIMISTIC BUMPS. A cook taps BUMP with a full hand and looks away. The
//    card must clear on the tap, not on the round trip. We apply the expected
//    state locally, fire the request, and reconcile with whatever the server
//    returns — including rolling back if it refuses.
// ─────────────────────────────────────────────────────────────────────────────

export type ConnStatus = 'connecting' | 'live' | 'degraded' | 'polling';

/** Which piece of paper a print button asks for. */
export type PrintKind = 'receipt' | 'kitchen';

/**
 * The headline is a dictionary KEY, not a string: the store has no language of
 * its own, and a toast raised in English that is still on screen after the cook
 * switches to Arabic would be the only untranslated thing on the board.
 */
export interface KdsToast {
  id: number;
  tone: 'ok' | 'error';
  titleKey: 'sent' | 'printFailed';
  detail: string;
}

/** Ping every 20s from the server; three missed = something is wrong. */
const PING_TIMEOUT_MS = 65_000;
const POLL_INTERVAL_MS = 5_000;

interface KdsState {
  ready: boolean;
  error: string | null;

  stations: KdsStation[];
  settings: KdsSettings | null;
  branchName: string | null;

  stationId: number | null;
  viewMode: KdsViewMode;

  /**
   * The business day on the board, 'YYYY-MM-DD' in the TENANT's timezone.
   *
   * Null until the first response, and that is the point: the tablet does not
   * decide what "today" is. The first fetch sends no date, the server answers
   * with the day it drew, and the picker starts there. A tablet whose clock is
   * a day out therefore still opens on the branch's today.
   */
  boardDate: string | null;

  /**
   * The tenant's today as the SERVER sees it. Sent on every board response, so
   * a screen left running overnight learns about the rollover instead of
   * quietly becoming a board of yesterday.
   */
  serverToday: string | null;

  /** Keyed by ticket id. A Map so a push is an O(1) replace, not an array scan. */
  tickets: Map<number, KdsTicket>;
  allDay: KdsAllDayRow[];
  stationCounts: Map<number, { open: number; late: number }>;

  conn: ConnStatus;
  /** serverNow - clientNow, in ms. See note 1 above. */
  skewMs: number;

  /** Ids the user has acted on but the server has not confirmed. */
  pending: Set<number>;

  /** Keys of the form `<ticketId>:<kind>` for every print in flight. */
  printing: Set<string>;
  /** Short-lived confirmations for actions with no visible effect on the board. */
  toasts: KdsToast[];

  init: () => Promise<void>;
  selectStation: (stationId: number | null, viewMode?: KdsViewMode) => Promise<void>;
  setViewMode: (mode: KdsViewMode) => Promise<void>;
  /** Show another day. Clears the board and refetches. */
  setBoardDate: (date: string) => Promise<void>;
  refresh: (opts?: { force?: boolean }) => Promise<void>;
  disconnect: () => void;

  start: (ticketId: number) => Promise<void>;
  bump: (ticketId: number) => Promise<void>;
  /**
   * Serve, optionally naming the driver who took the bag. `driverId` is only
   * ever supplied for a delivery order — see ServeDialog.
   */
  serve: (ticketId: number, driverId?: number | null) => Promise<void>;
  recall: (ticketId: number) => Promise<void>;
  setPriority: (ticketId: number, priority: KdsPriority) => Promise<void>;
  setHold: (ticketId: number, held: boolean) => Promise<void>;
  setLine: (ticketId: number, lineId: number, status: KdsLineStatus) => Promise<void>;
  fireCourse: (orderId: number, courseNo: number) => Promise<void>;
  printTicket: (ticketId: number, kind: PrintKind) => Promise<void>;
  dismissToast: (id: number) => void;
}

// Module-level handles rather than store state: they are not rendered, and
// putting a live EventSource in a zustand store means every subscriber
// re-renders when it changes.
let stream: api.StreamHandle | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pingWatchdog: ReturnType<typeof setTimeout> | null = null;
let lastSeenTicketIds = new Set<number>();

export const useKdsStore = create<KdsState>((set, get) => {
  // ── internals ─────────────────────────────────────────────────────────────

  function noteServerTime(serverTime: string): void {
    const server = new Date(serverTime).getTime();
    if (Number.isNaN(server)) return;
    set({ skewMs: server - Date.now() });
  }

  function handleAuthError(err: unknown): boolean {
    if (err instanceof AuthRequiredError) {
      useAuthStore.getState().onAuthExpired();
      return true;
    }
    return false;
  }

  /**
   * A ticket leaves the board when it is closed. `served` and `cancelled` are
   * terminal; `ready` stays visible on a prep station so the cook can undo a
   * mis-tap, and is the primary state on expo.
   */
  function shouldDrop(ticket: KdsTicket, viewMode: KdsViewMode): boolean {
    if (ticket.status === 'cancelled') return false; // keep, struck through
    if (ticket.status === 'served') return viewMode !== 'expo';
    return false;
  }

  function applyEvent(event: KdsStreamEvent): void {
    const state = get();
    switch (event.type) {
      case 'ping':
      case 'hello':
        noteServerTime(event.serverTime);
        armPingWatchdog();
        if (state.conn !== 'live') set({ conn: 'live' });
        stopPolling();
        return;

      case 'settings':
        set({ settings: event.settings });
        return;

      case 'stations':
        set({ stations: event.stations });
        return;

      case 'removed': {
        const next = new Map(state.tickets);
        next.delete(event.ticketId);
        set({ tickets: next });
        return;
      }

      case 'ticket': {
        const ticket = event.ticket;
        // Filter here as well as on the server: an expo subscriber receives
        // every station's events (it cannot know from the station id alone
        // whether it holds the order), so prep-station tickets arrive that this
        // board should not draw.
        if (!belongsOnThisBoard(ticket, state)) {
          if (state.tickets.has(ticket.id)) {
            const next = new Map(state.tickets);
            next.delete(ticket.id);
            set({ tickets: next });
          }
          // A prep bump still changes the expo card's progress bar, and that
          // lives on a DIFFERENT ticket we do hold. Re-read rather than guess.
          if (state.viewMode === 'expo' || state.viewMode === 'all') {
            void get().refresh();
          }
          return;
        }

        const next = new Map(state.tickets);
        if (shouldDrop(ticket, state.viewMode)) next.delete(ticket.id);
        else next.set(ticket.id, ticket);
        set({ tickets: next });

        // Chime on genuinely new work, never on a status change to something
        // we already had. A board that beeps every time a cook bumps is a board
        // whose speaker gets unplugged on day one.
        if (!lastSeenTicketIds.has(ticket.id) && ticket.status === 'queued') {
          lastSeenTicketIds.add(ticket.id);
          if (state.settings?.soundEnabled) chime('new');
        }
        lastSeenTicketIds.add(ticket.id);
        return;
      }
    }
  }

  /**
   * Is the board showing the live day?
   *
   * Both sides come from the server, so a tablet with a wrong clock cannot get
   * this wrong. Unknown (either missing) counts as live: the fallback has to be
   * "show the cook the new ticket", never "hide it".
   */
  function showingToday(state: KdsState): boolean {
    if (!state.boardDate || !state.serverToday) return true;
    return state.boardDate === state.serverToday;
  }

  function belongsOnThisBoard(ticket: KdsTicket, state: KdsState): boolean {
    // A ticket pushed over the stream was fired just now, so it belongs to
    // today. While the cook is looking at an earlier day it must not appear —
    // but an UPDATE to a card already on screen still must, or bumping a
    // yesterday ticket would leave it frozen. Hence: only NEW arrivals are
    // filtered by the day.
    if (!showingToday(state) && !state.tickets.has(ticket.id)) return false;
    if (state.viewMode === 'station') return ticket.stationId === state.stationId;
    if (state.viewMode === 'expo') return ticket.stationType !== 'prep';
    return true; // 'all'
  }

  function armPingWatchdog(): void {
    if (pingWatchdog) clearTimeout(pingWatchdog);
    pingWatchdog = setTimeout(() => {
      // Pings stopped. The stream may still be "open" as far as the browser is
      // concerned — a proxy that silently dropped the connection looks exactly
      // like a quiet kitchen — so we start polling rather than wait to be told.
      set({ conn: 'polling' });
      startPolling();
    }, PING_TIMEOUT_MS);
  }

  function startPolling(): void {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      if (document.hidden) return;
      void get().refresh();
    }, POLL_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (!pollTimer) return;
    clearInterval(pollTimer);
    pollTimer = null;
  }

  function connect(): void {
    stream?.close();
    const { stationId, viewMode } = get();
    set({ conn: 'connecting' });
    stream = api.openStream(
      stationId,
      viewMode,
      applyEvent,
      (status) => {
        if (status === 'open') {
          set({ conn: 'live' });
          armPingWatchdog();
          stopPolling();
        } else {
          // EventSource fires 'error' on every transient blip while it retries.
          // Degraded, not dead: keep the board, start polling so the screen
          // stays truthful, and let the watchdog/ping promote us back to live.
          set({ conn: 'degraded' });
          startPolling();
        }
      },
    );
  }

  /** Replace one ticket, honouring the drop rules. */
  function upsert(ticket: KdsTicket): void {
    const state = get();
    const next = new Map(state.tickets);
    if (shouldDrop(ticket, state.viewMode) || !belongsOnThisBoard(ticket, state)) {
      next.delete(ticket.id);
    } else {
      next.set(ticket.id, ticket);
    }
    set({ tickets: next });
  }

  /**
   * Apply an expected state immediately, run the request, then reconcile.
   *
   * On failure we put the original ticket back rather than leaving the screen
   * showing something that did not happen — a cook who sees a card clear and
   * then quietly reappear will at least re-read it; one who sees it clear and
   * stay cleared will not cook the food.
   */
  async function optimistic(
    ticketId: number,
    predict: (t: KdsTicket) => KdsTicket,
    call: () => Promise<{ ticket: KdsTicket }>,
  ): Promise<void> {
    const before = get().tickets.get(ticketId);
    if (before) upsert(predict(before));

    const pending = new Set(get().pending);
    pending.add(ticketId);
    set({ pending });

    try {
      const { ticket } = await call();
      upsert(ticket);
    } catch (err) {
      if (before) upsert(before);
      if (!handleAuthError(err)) {
        set({ error: err instanceof Error ? err.message : String(err) });
      }
    } finally {
      const after = new Set(get().pending);
      after.delete(ticketId);
      set({ pending: after });
    }
  }

  // ── public ────────────────────────────────────────────────────────────────

  return {
    ready: false,
    error: null,
    stations: [],
    settings: null,
    branchName: null,
    stationId: null,
    viewMode: 'station',
    boardDate: null,
    serverToday: null,
    tickets: new Map(),
    allDay: [],
    stationCounts: new Map(),
    conn: 'connecting',
    skewMs: 0,
    pending: new Set(),
    printing: new Set(),
    toasts: [],

    init: async () => {
      try {
        const boot = await api.fetchBootstrap(api.deviceKey());
        noteServerTime(boot.serverTime);
        // Seeds the default language; a per-screen override in localStorage
        // still wins, so a bilingual branch can run one station in each.
        setBranchLanguage(boot.settings.language);

        // What this screen was showing last time, in preference order: the
        // device's saved binding, then whatever is stored locally, then the
        // first prep station. A wall-mounted tablet should never greet the
        // morning shift with a picker.
        const savedStation = boot.device?.stationId ?? readLocalStation();
        const savedView = boot.device?.viewMode ?? readLocalView();
        const stationId =
          savedStation && boot.stations.some((s) => s.id === savedStation)
            ? savedStation
            : boot.stations.find((s) => s.type === 'prep')?.id ?? null;

        set({
          stations: boot.stations,
          settings: boot.settings,
          branchName: boot.branchName,
          stationId,
          viewMode: savedView ?? 'station',
          ready: true,
          error: null,
        });

        await get().refresh();
        connect();
      } catch (err) {
        if (!handleAuthError(err)) {
          set({
            error: err instanceof Error ? err.message : String(err),
            ready: true,
          });
        }
      }
    },

    selectStation: async (stationId, viewMode) => {
      const mode = viewMode ?? get().viewMode;
      set({ stationId, viewMode: mode, tickets: new Map() });
      writeLocalStation(stationId);
      writeLocalView(mode);
      lastSeenTicketIds = new Set();
      void api
        .registerDevice({ deviceKey: api.deviceKey(), stationId, viewMode: mode })
        .catch(() => {
          // Remembering the binding is a nicety; failing it must not block the
          // cook from switching station.
        });
      // force: the line above emptied the board, so a 304 here would leave it
      // empty. See the note on refresh.
      await get().refresh({ force: true });
      connect();
    },

    setViewMode: async (mode) => {
      await get().selectStation(get().stationId, mode);
    },

    setBoardDate: async (date) => {
      if (date === get().boardDate) return;
      // Same shape as a view switch, and the same trap: the board is emptied
      // here, so the refetch MUST bypass the ETag or a 304 would leave it that
      // way. See the note on refresh.
      set({ boardDate: date, tickets: new Map() });
      lastSeenTicketIds = new Set();
      await get().refresh({ force: true });
    },

    /**
     * Reload the current view.
     *
     * `force` is not an optimisation switch — it is mandatory whenever the
     * caller has just emptied `tickets`. A 304 means "keep what you have", and
     * the early return below honours that faithfully; if what we have is an
     * empty Map that we cleared a millisecond ago, the board stays blank and
     * only a re-login (which clears the ETag cache) brings it back. That was
     * the "click الكل and every order disappears" bug.
     */
    refresh: async (opts = {}) => {
      const { stationId, viewMode } = get();
      try {
        if (viewMode === 'allday') {
          const result = await api.fetchAllDay(stationId, opts);
          if (result === NOT_MODIFIED) return;
          noteServerTime(result.serverTime);
          set({ allDay: result.rows, error: null });
          return;
        }

        const result =
          viewMode === 'recall'
            ? await api.fetchRecall(stationId, opts)
            : await api.fetchBoard(viewMode === 'station' ? stationId : null, viewMode, {
                ...opts,
                date: get().boardDate,
              });

        if (result === NOT_MODIFIED) return;
        noteServerTime(result.serverTime);

        const tickets = new Map<number, KdsTicket>();
        for (const t of result.tickets) tickets.set(t.id, t);
        // Seed the seen-set from the first load so re-opening the screen on a
        // full board does not fire twenty chimes at once.
        for (const t of result.tickets) lastSeenTicketIds.add(t.id);

        const counts = new Map<number, { open: number; late: number }>();
        for (const c of result.stationCounts) {
          counts.set(c.stationId, { open: c.open, late: c.late });
        }

        set({
          tickets,
          stationCounts: counts,
          error: null,
          // The server is the authority on which day this is; on the first
          // load it is the only thing that knows.
          ...(result.boardDate ? { boardDate: result.boardDate } : {}),
          ...(result.serverToday ? { serverToday: result.serverToday } : {}),
        });
      } catch (err) {
        if (!handleAuthError(err)) {
          set({ error: err instanceof Error ? err.message : String(err) });
        }
      }
    },

    disconnect: () => {
      stream?.close();
      stream = null;
      stopPolling();
      if (pingWatchdog) clearTimeout(pingWatchdog);
      pingWatchdog = null;
    },

    start: (id) =>
      optimistic(id, (t) => ({ ...t, status: 'preparing', startedAt: new Date().toISOString() }),
        () => api.startTicket(id)),

    bump: (id) =>
      optimistic(id, (t) => ({ ...t, status: 'ready', readyAt: new Date().toISOString() }),
        () => api.bumpTicket(id)),

    serve: (id, driverId) =>
      optimistic(id, (t) => ({ ...t, status: 'served', servedAt: new Date().toISOString() }),
        () => api.serveTicket(id, driverId ?? null)),

    recall: (id) =>
      optimistic(id, (t) => ({ ...t, status: 'recalled', readyAt: null, recallCount: t.recallCount + 1 }),
        () => api.recallTicket(id)),

    setPriority: (id, priority) =>
      optimistic(id, (t) => ({ ...t, priority }), () => api.setTicketPriority(id, priority)),

    setHold: (id, held) =>
      optimistic(id, (t) => ({ ...t, isHeld: held }), () => api.setTicketHold(id, held)),

    setLine: (ticketId, lineId, status) =>
      optimistic(
        ticketId,
        (t) => ({
          ...t,
          lines: t.lines.map((l) =>
            l.id === lineId
              ? { ...l, status, qtyReady: status === 'ready' ? l.qty : 0 }
              : l,
          ),
        }),
        () => api.setLineStatus(lineId, status),
      ),

    fireCourse: async (orderId, courseNo) => {
      try {
        const { tickets } = await api.fireCourse(orderId, courseNo);
        for (const t of tickets) upsert(t);
      } catch (err) {
        if (!handleAuthError(err)) {
          set({ error: err instanceof Error ? err.message : String(err) });
        }
      }
    },

    /**
     * Reprint, with an answer.
     *
     * Every other action on this board changes the card, so the cook can see it
     * worked. A print changes nothing on screen — the only feedback is paper
     * that may be thirty seconds and one room away. So this one reports: where
     * it went, or why it went nowhere. It also does NOT set `error`, which
     * would put a banner across the top of the board for something that
     * concerns one ticket.
     */
    printTicket: async (ticketId, kind) => {
      const key = `${ticketId}:${kind}`;
      if (get().printing.has(key)) return; // double-tap on a laggy tablet
      set({ printing: new Set(get().printing).add(key) });

      try {
        if (kind === 'kitchen') {
          // The kitchen slip has to come out of the printer IN THE KITCHEN, so
          // it goes to the server and out through SCPS. A branch with no
          // printer configured genuinely cannot do this.
          const result = await api.printTicketKitchen(ticketId);
          const where = result.printers.length > 0 ? result.printers.join(', ') : '';
          pushToast(set, get, 'ok', 'sent', where);
        } else {
          // The customer invoice is handed to the person standing here, so it
          // prints from this browser exactly as the till's does — no SCPS, no
          // per-branch printer rows, nothing to configure. See printInvoice.ts.
          const ticket = get().tickets.get(ticketId);
          if (!ticket) throw new Error('That ticket is no longer on the board.');
          // Loaded on demand: the receipt renderer and its QR library are ~95KB
          // that a kitchen screen boots without and most shifts never need. The
          // cook waits for it once, on a button they press occasionally, rather
          // than every tablet waiting for it on every load.
          const { printCustomerInvoice } = await import('../lib/printInvoice');
          await printCustomerInvoice(ticket.orderId);
          pushToast(set, get, 'ok', 'sent', '');
        }
      } catch (err) {
        if (!handleAuthError(err)) {
          pushToast(set, get, 'error', 'printFailed', printErrorDetail(err));
        }
      } finally {
        const after = new Set(get().printing);
        after.delete(key);
        set({ printing: after });
      }
    },

    dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
  };
});

let toastSeq = 0;

function pushToast(
  set: (partial: Partial<KdsState>) => void,
  get: () => KdsState,
  tone: 'ok' | 'error',
  titleKey: KdsToast['titleKey'],
  detail: string,
): void {
  const id = ++toastSeq;
  set({ toasts: [...get().toasts, { id, tone, titleKey, detail }] });
  // Errors linger: somebody has to read them and decide what to do.
  setTimeout(
    () => useKdsStore.setState((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
    tone === 'error' ? 8000 : 3500,
  );
}

/**
 * A failed print is usually a configuration problem at the tenant ("no printer
 * is configured for this branch"), which the API returns in `detail`. Surfacing
 * that verbatim is what turns "it didn't print" into a call someone can make.
 */
function printErrorDetail(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { detail?: string; error?: string } | null;
    if (body?.detail) return body.detail;
    if (body?.error) return body.error;
  }
  return err instanceof Error ? err.message : String(err);
}

// ── local persistence ───────────────────────────────────────────────────────
//
// Belt and braces alongside the server-side kds_device binding: the server
// remembers across devices, localStorage remembers across a server that is
// briefly unreachable, which is exactly when a tablet reboots.

const STATION_KEY = 'matix_kds_station';
const VIEW_KEY = 'matix_kds_view';

function readLocalStation(): number | null {
  const raw = localStorage.getItem(STATION_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function writeLocalStation(stationId: number | null): void {
  if (stationId == null) localStorage.removeItem(STATION_KEY);
  else localStorage.setItem(STATION_KEY, String(stationId));
}

function readLocalView(): KdsViewMode | null {
  const raw = localStorage.getItem(VIEW_KEY);
  const allowed: KdsViewMode[] = ['station', 'expo', 'all', 'allday', 'recall'];
  return allowed.includes(raw as KdsViewMode) ? (raw as KdsViewMode) : null;
}

function writeLocalView(mode: KdsViewMode): void {
  localStorage.setItem(VIEW_KEY, mode);
}

// ── selectors ───────────────────────────────────────────────────────────────

/**
 * Board order: rush and VIP first, then oldest-fired first.
 *
 * Deliberately NOT sorted by status. A ticket must not jump across the board
 * when it is bumped — cooks navigate by position ("mine is second from the
 * left") and a board that reflows on every action is a board nobody can work
 * from. Ready tickets are dimmed in place instead.
 */
export function sortedTickets(tickets: Map<number, KdsTicket>): KdsTicket[] {
  return [...tickets.values()].sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority;
    const at = new Date(a.firedAt).getTime();
    const bt = new Date(b.firedAt).getTime();
    if (at !== bt) return at - bt;
    return a.id - b.id;
  });
}
